import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Chat } from "../types/chat";
import { subscribeForChatUpdate } from "../api/firebase/firebaseChat";

const filterUnreadChats = (chats: Chat[], userId: number) => {
  return chats.filter((chat) => {
    if (chat.lastMessage.senderId === userId) return false;
    return chat.lastSeen?.[userId] < chat.lastMessage?.createdAt;
  });
};

const filterChatsBySearch = (chats: Chat[], searchValue: string, userId: number) => {
  return chats.filter((chat) => {
    if (chat.participants?.length > 1) {
      const receiverId = chat.participants.find((id) => id !== userId);
      if (receiverId && chat.participantsNames?.[receiverId]) {
        return chat.participantsNames[receiverId].toLowerCase().includes(searchValue.toLowerCase());
      }
    }
    return false;
  });
};

export const useGetChatList = (userId: number | null, searchValue: string) => {
  const [chats, setChats] = useState<Chat[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams] = useSearchParams();

  const isUnreadActive = useMemo(() => searchParams.get("tab") === "unread", [searchParams]);

  useEffect(() => {
    if (!userId) return;

    const handleChatUpdates = (chatList: Chat[]) => {
      setIsLoading(true);
      let filteredChats = chatList;

      if (isUnreadActive) {
        filteredChats = filterUnreadChats(filteredChats, userId);
      }

      if (searchValue) {
        filteredChats = filterChatsBySearch(filteredChats, searchValue, userId);
      }

      setChats(filteredChats);
      setIsLoading(false);
    };

    const setupSubscription = async () => {
      const unsubscribe = await subscribeForChatUpdate(userId, handleChatUpdates);
      return unsubscribe;
    };

    const unsubscribePromise = setupSubscription();
    return () => {
      unsubscribePromise.then((unsubscribe) => unsubscribe());
    };
  }, [userId, isUnreadActive, searchValue]);

  return { chats, isLoading };
};
